import React, { useCallback, useEffect, useRef, useState } from "react";
import SwitchTransition from "react-transition-group/SwitchTransition";
import styled from "styled-components";
import { Special } from "../../generated/graphql";
import dontForward from "../../utils/dontForward";
import flexCss from "../../utils/flexCss";
import valuesCss from "../../utils/valuesCss";
import isBrowserEnv from "../../utils/isBrowserEnv";
import FadeLeftRight from "../transitions/FadeLeftRight";
import OfferedPrice from "./OfferedPrice";

const PriceDisplayContainer = styled("section").withConfig(dontForward("highlighted"))<{ highlighted: boolean }>(({ theme, highlighted }) => ({
    ...flexCss("v"),
    minWidth: highlighted ? 86 : 80,
    "--price-bg": highlighted ? theme.palette.secondary.main : theme.palette.quaternary.main,
    "--price-text": highlighted ? theme.palette.secondary.contrastText : theme.palette.quaternary.contrastText,
    "--dealmobil-price-bg": "rgb(74, 173, 51)",
    "--dealmobil-price-text": "white",
    [theme.media("sm")]: {        
        minWidth: 100
    }
}));

const NormalPrice = styled("footer")(({ theme }) => ({
    textAlign: "center",
    background: "var(--price-bg)",
    color: "var(--price-text)",
    marginTop: -3,
    fontSize: 12,
    lineHeight: "1.5",
    fontWeight: theme.typography.bold,
    padding: "0 0.4em",
    [theme.media("sm")]: {        
        fontSize: 14
    }
}));

const OfferedPriceBlock = styled(OfferedPrice)(({ theme }) => ({
    background: theme.palette.white,
    color: theme.palette.black,
    border: "3px solid var(--price-bg)",
    padding: valuesCss("px", 4, 8, 6, 8),
    textAlign: "center",
    [theme.media("sm")]: {        
        fontSize: 36
    }
}));

const DealMobil = styled("footer")(({ theme }) => ({
    "-webkit-text-stroke-color": "var(--dealmobil-price-bg)",
    "-webkit-text-stroke-width": "1px",
    textAlign: "center",
    textShadow: "0 0 10px var(--dealmobil-price-bg)",
    color: "yellow",    
    position: "relative",
    top: 60,
    zIndex: 1,
    fontWeight: theme.typography.bold,
    padding: "0 0.4em"
}));

const DealMobilTrapez = styled("div")(({ theme }) => ({
    width: 25,
    height: 25,
    background: "linear-gradient(rgb(74, 173, 51) 0%, rgb(74, 173, 51) 33%, rgb(44, 125, 38) 100%)",
    "clip-path": "polygon(100% 100%, 0% 100%, 100% 0%)",
    position: "relative",
    top: 41,
    left: -15,
    [theme.media("sm")]: {
        top: 51.4
    }
}));

const DealMobilPriceBlock = styled(OfferedPrice)(({ theme }) => ({
    background: "linear-gradient(180deg, rgba(74, 173, 51, 1) 0%, rgba(74, 173, 51, 1) 66%, rgba(44, 125, 38, 1) 100%);",
    color: "white",
    borderRadius: 10,
    padding: valuesCss("px", 8, 8, 6, 8),
    marginBottom: 4,
    textAlign: "center",
    [theme.media("sm")]: {        
        fontSize: 36
    }
}));

const priceFormat =  new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

let globalIntervalId: number | undefined;
const GlobalIntervalEvent = isBrowserEnv() && new Event("globalInterval");

const ThreeDaysPriceText: React.FC = () => {

    const [flip, setFlip] = useState(false);
    const nodeRef = useRef(null);

    const toggleFlip = useCallback(() => setFlip(!flip), [flip]);

    useEffect(() => {
        if (!globalIntervalId) {
            globalIntervalId = window.setInterval(() => {
                if (GlobalIntervalEvent) {
                    document.dispatchEvent(GlobalIntervalEvent);
                }
            }, 6000);
        }

        document.addEventListener("globalInterval", toggleFlip);

        return () => document.removeEventListener("globalInterval", toggleFlip);

    }, [toggleFlip]);

    return (
        <SwitchTransition mode="out-in">
            <FadeLeftRight key={flip.toString()} nodeRef={nodeRef}>
                <span ref={nodeRef}>{flip ? "DO bis SA" : "3-Tage-Preis"}</span>
            </FadeLeftRight>
        </SwitchTransition>
    );
};

interface IPriceDisplayProps {
    offered: number;
    normal: number | null;
    hideNormalPrice: boolean;
    special?: Special | null;
    className?: string;
    dealMobilPrice?: number | null;
}

const PriceDisplay: React.FC<IPriceDisplayProps> = ({ offered, normal, hideNormalPrice, special, className, dealMobilPrice }) => (
    <PriceDisplayContainer className={className} highlighted={special === Special.BigHit || special === Special.MondayBigHit || special === Special.ThreeDaysPrice || special === Special.SaturdayBigHit}>
        {dealMobilPrice !== 0 && (
            <>
                <DealMobil>
                    <h2>DealMobil</h2>
                </DealMobil>
                <DealMobilTrapez />
                <DealMobilPriceBlock price={dealMobilPrice} skew={true} />
            </>
        )}   
        {special === Special.BigHit ?
            <NormalPrice><span>Knüller</span></NormalPrice> :
            null}
        <OfferedPriceBlock price={offered} skew={true} />
        <NormalPrice>
            {special === Special.MondayBigHit ? (
                <span>Montagsknüller</span>
            ) : special === Special.ThreeDaysPrice ? (
                <ThreeDaysPriceText />
            ) : special === Special.SaturdayBigHit ? (
                <span>Samstagskracher</span>
            ) : special === Special.OnlyNormal ? (
                <span></span>
            ) : (hideNormalPrice && normal || offered == normal) ? (
                <span>Aktionspreis</span>
            ) : !normal ? (
                <span></span>
            ) : (
                <><span>statt</span> {normal && priceFormat.format(normal)}</>
            )}
        </NormalPrice>
    </PriceDisplayContainer>
);

export default PriceDisplay;